import { useState, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import DOMPurify from 'dompurify'
import { cva } from 'class-variance-authority'
import { Copy, Check, Volume2, Square } from 'lucide-react'
import { cn } from '@/shared/lib/cn'
import { parseApiDateTime } from '@/shared/lib/formatters'
import { synthesizeText } from '@/shared/lib/api'
import type { ChatMessage as ChatMessageType, ContentBlock, MessageMeta } from '@/shared/types'
import { InlineToolCall } from './ToolCall'
import { InlineToolResult } from './ToolResult'
import { ToolCallGroup } from './ToolCallGroup'
import { ArtifactBlock } from './ArtifactBlock'
import { AskUserPrompt } from './AskUserPrompt'
import { markInputAnswered } from '../hooks/useChat'
import { MODEL_LABELS } from './ChatInput'

const messageVariants = cva(
  'p-3 rounded-xl max-w-[85%] text-sm break-words',
  {
    variants: {
      sender: {
        user: 'bg-[#5E6AD2] text-white ml-auto rounded-br-sm',
        ai: 'bg-white/5 border border-white/5 text-[#EEEEEE] mr-auto rounded-bl-sm',
      },
    },
  }
)

const markdownClass =
  'prose-sm prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_code]:bg-white/10 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-white/10 [&_pre]:p-2 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_strong]:text-white [&_a]:text-[#8B96E9] [&_a]:underline [&_hr]:border-white/10 [&_blockquote]:border-l-2 [&_blockquote]:border-white/20 [&_blockquote]:pl-3 [&_blockquote]:text-white/60'

// ── Message metadata footer ───────────────────────────────────────────────────

function MetaFooter({ meta }: { meta: MessageMeta }) {
  const parts: string[] = []

  if (meta.model) {
    const short = MODEL_LABELS[meta.model] ?? meta.model.replace('claude-', '').replace(/-\d{8}$/, '')
    parts.push(short)
  }

  if (meta.durationMs != null) {
    const secs = (meta.durationMs / 1000).toFixed(1)
    parts.push(`${secs}s`)
  }

  if (meta.usage) {
    const inTok = meta.usage.input_tokens
    const outTok = meta.usage.output_tokens
    if (inTok || outTok) {
      const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n)
      parts.push(`${fmt(inTok)}→${fmt(outTok)} tok`)
    }
  }

  if (meta.totalCostUsd != null && meta.totalCostUsd > 0) {
    parts.push(`$${meta.totalCostUsd.toFixed(4)}`)
  }

  if (parts.length === 0) return null

  return (
    <span className="text-[10px] text-white/20 font-mono">
      {parts.join(' · ')}
    </span>
  )
}

// ── Agent chip ─────────────────────────────────────────────────────────────────

function AgentChip({ agent }: { agent: { name: string; display_name: string; description: string } }) {
  const agentColors: Record<string, string> = {
    workout_agent: 'bg-white/10 text-white/70 border-white/20',
    router_agent: 'bg-white/10 text-white/70 border-white/20',
    tracker_agent: 'bg-white/10 text-white/70 border-white/20',
    assistant_agent: 'bg-emerald-500/15 text-emerald-300/80 border-emerald-400/25',
  }

  const colorClass = agentColors[agent.name] || 'bg-white/10 text-white/60 border-white/10'

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${colorClass} text-xs font-medium max-w-full`}
      title={agent.description}
    >
      <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
      </svg>
      <span className="truncate">{agent.display_name}</span>
    </div>
  )
}

// ── Timestamp ──────────────────────────────────────────────────────────────────

function MessageTimestamp({ timestamp }: { timestamp: string }) {
  const time = parseApiDateTime(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return (
    <span className="text-[10px] text-white/25 mt-1 block">
      {time}
    </span>
  )
}

// ── Copy Button ────────────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-1 mt-1 text-[10px] text-white/30 hover:text-white/60 hover:bg-white/5 rounded transition-colors"
      title="Copy message"
    >
      {copied ? (
        <>
          <Check size={11} />
          <span>Copied</span>
        </>
      ) : (
        <>
          <Copy size={11} />
          <span>Copy</span>
        </>
      )}
    </button>
  )
}

// ── Speak Button ───────────────────────────────────────────────────────────────

function SpeakButton({ text }: { text: string }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const sourceRef = useRef<AudioBufferSourceNode | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)

  const stopPlayback = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop() } catch { /* already stopped */ }
      sourceRef.current = null
    }
    setIsPlaying(false)
  }, [])

  const handleClick = async () => {
    if (isPlaying) {
      stopPlayback()
      return
    }

    setIsLoading(true)
    try {
      const wavBuffer = await synthesizeText(text)

      const ctx = audioCtxRef.current ?? new AudioContext()
      audioCtxRef.current = ctx
      if (ctx.state === 'suspended') await ctx.resume()

      const audioBuffer = await ctx.decodeAudioData(wavBuffer)
      const source = ctx.createBufferSource()
      source.buffer = audioBuffer
      source.connect(ctx.destination)
      sourceRef.current = source

      source.onended = () => {
        sourceRef.current = null
        setIsPlaying(false)
      }

      source.start()
      setIsPlaying(true)
    } catch (err) {
      console.error('[speak-button] TTS failed:', err)
      // Fallback to browser speech synthesis
      if (window.speechSynthesis) {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.rate = 1.05
        setIsPlaying(true)
        utterance.onend = () => setIsPlaying(false)
        utterance.onerror = () => setIsPlaying(false)
        window.speechSynthesis.speak(utterance)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className="inline-flex items-center gap-1 px-2 py-1 mt-1 text-[10px] text-white/30 hover:text-white/60 hover:bg-white/5 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      title={isPlaying ? 'Stop speaking' : 'Speak message'}
    >
      {isPlaying ? (
        <>
          <Square size={11} />
          <span>Stop</span>
        </>
      ) : (
        <>
          <Volume2 size={11} />
          <span>Speak</span>
        </>
      )}
    </button>
  )
}

// ── Subagent summary (collapsed by default, expand to see all events) ────────

function SubagentSummary({ count, currentTool, events }: { count: number; currentTool: string | null; events: ContentBlock[] }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="my-1.5 ml-4 rounded-lg border border-white/10 bg-white/[0.03] overflow-hidden text-xs max-w-[85%]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-white/5 transition-colors"
      >
        <svg
          className={cn('w-3 h-3 flex-shrink-0 text-white/30 transition-transform', expanded && 'rotate-90')}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
        <span className="text-white/25 font-mono">↳</span>
        <span className="text-white/50">
          sub-agent · {count} event{count !== 1 ? 's' : ''}
        </span>
        {currentTool && !expanded && (
          <span className="text-white/30 font-mono truncate">({currentTool})</span>
        )}
      </button>

      {expanded && (
        <div className="border-t border-white/5 px-2 py-1.5 max-h-80 overflow-y-auto space-y-1">
          {events.map((ev, j) => {
            if (ev.type === 'subagent_tool_call') {
              return (
                <div key={j} className="border-l border-white/10 pl-2">
                  <InlineToolCall name={ev.name} args={ev.args} />
                </div>
              )
            }
            if (ev.type === 'tool_result') {
              return (
                <div key={j} className="border-l border-white/10 pl-2">
                  <InlineToolResult content={ev.content} isError={ev.is_error} />
                </div>
              )
            }
            if (ev.type === 'subagent_text' && ev.content.trim()) {
              return (
                <div key={j} className="border-l border-white/10 pl-2">
                  <div className={cn(messageVariants({ sender: 'ai' }), 'bg-white/[0.02]')}>
                    <div className={markdownClass}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {DOMPurify.sanitize(ev.content)}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )
            }
            return null
          })}
        </div>
      )}
    </div>
  )
}

// ── ChatMessage ────────────────────────────────────────────────────────────────

interface ChatMessageProps {
  message: ChatMessageType
  conversationId: number
}

export function ChatMessage({ message, conversationId }: ChatMessageProps) {
  const sanitizedText = DOMPurify.sanitize(message.text)

  // ── User message ────────────────────────────────────────────────────────────
  if (message.sender === 'user') {
    // Separate image attachments for thumbnail rendering
    const imageAttachments = message.attachments?.filter(a => a.isImage) ?? []

    return (
      <div className="mb-4 flex flex-col items-end group pb-1">
        <div className={cn(messageVariants({ sender: 'user' }))}>
          {/* Image attachment thumbnails */}
          {imageAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {imageAttachments.map((attachment, idx) => (
                <img
                  key={idx}
                  src={attachment.dataUrl}
                  alt={attachment.name}
                  className="max-w-[120px] max-h-[120px] rounded-lg object-cover border border-white/20"
                />
              ))}
            </div>
          )}
          {/* Text content (may include [Attached: ...] for non-image files) */}
          {message.text && <span className="whitespace-pre-wrap">{message.text}</span>}
        </div>
        <div className="flex items-center gap-2">
          {message.timestamp && <MessageTimestamp timestamp={message.timestamp} />}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
            <CopyButton text={message.text} />
            <SpeakButton text={message.text} />
          </div>
        </div>
      </div>
    )
  }

  // ── AI message with ordered blocks (streamed messages) ───────────────────────
  if (message.blocks && message.blocks.length > 0) {
    // Build a map of tool_use_id → tool_result for nesting results inside tool calls
    const resultMap = new Map<string, Extract<ContentBlock, { type: 'tool_result' }>>()
    message.blocks.forEach((b: ContentBlock) => {
      if (b.type === 'tool_result') resultMap.set(b.tool_use_id, b)
    })

    // Extract text content from blocks for copying
    const textContent = message.blocks
      .filter((block): block is Extract<ContentBlock, { type: 'text' }> => block.type === 'text')
      .map(block => block.content)
      .join('\n\n')

    // Group consecutive parent-level tool_call blocks together
    type ToolCallWithResult = {
      block: Extract<ContentBlock, { type: 'tool_call' }>
      index: number
      result: Extract<ContentBlock, { type: 'tool_result' }> | null
    }

    type GroupedBlock =
      | { type: 'single'; block: ContentBlock; index: number }
      | { type: 'tool_group'; calls: ToolCallWithResult[] }

    const groupedBlocks: GroupedBlock[] = []
    let currentToolGroup: ToolCallWithResult[] | null = null

    message.blocks.forEach((block: ContentBlock, i: number) => {
      if (block.type === 'tool_call') {
        const matchingResult = resultMap.get(block.id) || null
        if (!currentToolGroup) {
          currentToolGroup = []
        }
        currentToolGroup.push({ block, index: i, result: matchingResult })
      } else {
        // End current tool group if exists
        if (currentToolGroup && currentToolGroup.length > 0) {
          groupedBlocks.push({ type: 'tool_group', calls: currentToolGroup })
          currentToolGroup = null
        }
        // Skip tool_result blocks (they're nested in tool_call)
        if (block.type !== 'tool_result') {
          groupedBlocks.push({ type: 'single', block, index: i })
        }
      }
    })
    // Flush any remaining tool group
    if (currentToolGroup) {
      groupedBlocks.push({ type: 'tool_group', calls: currentToolGroup })
    }

    return (
      <div className="mb-4 group pb-1">
        {message.agent && (
          <div className="flex flex-wrap mb-2">
            <AgentChip agent={message.agent} />
          </div>
        )}
        <div className="space-y-1">
          {groupedBlocks.map((grouped, gIdx) => {
            if (grouped.type === 'tool_group') {
              // Render multiple consecutive tool calls as a group
              if (grouped.calls.length === 1) {
                // Single tool call - check if it's a render_artifact call
                const { block, index, result } = grouped.calls[0]
                if (block.name === 'render_artifact' && result && !result.is_error) {
                  // Try to parse artifact data from the result or fall back to args
                  let artifactData: { title?: string; content?: string; language?: string; id?: string } | null = null
                  try {
                    const parsed = JSON.parse(result.content)
                    if (parsed && typeof parsed === 'object' && parsed.content) {
                      artifactData = parsed
                    }
                  } catch {
                    // result content isn't JSON — fall back to args
                  }
                  if (!artifactData && block.args.content) {
                    artifactData = block.args as { title?: string; content?: string; language?: string; id?: string }
                  }
                  if (
                    artifactData &&
                    typeof artifactData.content === 'string' &&
                    (artifactData.language === 'html' || artifactData.language === 'mermaid' || artifactData.language === 'markdown')
                  ) {
                    return (
                      <ArtifactBlock
                        key={index}
                        id={artifactData.id}
                        title={artifactData.title ?? 'Artifact'}
                        content={artifactData.content}
                        language={artifactData.language}
                      />
                    )
                  }
                }
                // Default: render inline tool call
                return (
                  <InlineToolCall
                    key={index}
                    name={block.name}
                    args={block.args}
                    result={result ? { content: result.content, isError: result.is_error } : null}
                  />
                )
              } else {
                // Multiple tool calls - render as grouped
                return (
                  <ToolCallGroup
                    key={`group-${gIdx}`}
                    calls={grouped.calls.map(c => ({ name: c.block.name, args: c.block.args }))}
                  />
                )
              }
            }

            // Single non-tool block
            const block = grouped.block
            const i = grouped.index

            if (block.type === 'text') {
              if (!block.content.trim()) return null
              return (
                <div key={i} className={cn(messageVariants({ sender: 'ai' }))}>
                  <div className={markdownClass}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {DOMPurify.sanitize(block.content)}
                    </ReactMarkdown>
                  </div>
                </div>
              )
            }
            if (block.type === 'artifact') {
              return (
                <ArtifactBlock
                  key={i}
                  id={block.id}
                  title={block.title}
                  content={block.content}
                  language={block.language}
                />
              )
            }
            if (block.type === 'subagent_summary') {
              return <SubagentSummary key={i} count={block.count} currentTool={block.currentTool} events={block.events} />
            }
            if (block.type === 'subagent_tool_call') {
              return (
                <div key={i} className="ml-4 border-l border-white/10 pl-2">
                  <InlineToolCall name={block.name} args={block.args} />
                </div>
              )
            }
            if (block.type === 'subagent_text') {
              if (!block.content.trim()) return null
              return (
                <div key={i} className="ml-4 border-l border-white/10 pl-2">
                  <div className="text-[10px] text-white/25 mb-0.5 font-mono">↳ sub-agent</div>
                  <div className={cn(messageVariants({ sender: 'ai' }), 'bg-white/[0.02]')}>
                    <div className={markdownClass}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {DOMPurify.sanitize(block.content)}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              )
            }
            if (block.type === 'user_input_prompt') {
              return (
                <AskUserPrompt
                  key={i}
                  conversationId={conversationId}
                  toolUseId={block.tool_use_id}
                  question={block.question}
                  answered={block.answered}
                  answer={block.answer}
                  onAnswered={(answer) => markInputAnswered(conversationId, block.tool_use_id, answer)}
                />
              )
            }
            return null
          })}
        </div>
        <div className="flex items-center gap-2">
          {message.timestamp && <MessageTimestamp timestamp={message.timestamp} />}
          {message.meta && <MetaFooter meta={message.meta} />}
          {textContent && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
              <CopyButton text={textContent} />
              <SpeakButton text={textContent} />
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── AI message without blocks (history / no tool calls) ──────────────────────
  // Skip rendering the empty placeholder that exists before streaming starts
  const hasContent = message.text.trim() || (message.functionCalls && message.functionCalls.length > 0)
  if (!hasContent) return null

  return (
    <div className="mb-4 group pb-1">
      {message.agent && (
        <div className="flex flex-wrap mb-2">
          <AgentChip agent={message.agent} />
        </div>
      )}
      {message.functionCalls && message.functionCalls.length > 0 && (
        <ToolCallGroup calls={message.functionCalls as Array<{ name: string; args: Record<string, unknown> }>} />
      )}
      <div className={cn(messageVariants({ sender: 'ai' }))}>
        <div className={markdownClass}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {sanitizedText}
          </ReactMarkdown>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {message.timestamp && <MessageTimestamp timestamp={message.timestamp} />}
        {message.meta && <MetaFooter meta={message.meta} />}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
          <CopyButton text={message.text} />
          <SpeakButton text={message.text} />
        </div>
      </div>
    </div>
  )
}
